import React, { useState } from "react";
import { PendulumLogo } from "../../../../api/giftCategories";
import useUpdateLogoMutation from "../hooks/useUpdateLogoMutation";
import useDeleteLogoMutation from "../hooks/useDeleteLogoMutation";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSBox from "../../../system-design/DSBox";
import DSStack from "../../../system-design/DSStack";
import DSTypography from "../../../system-design/DSTypography";
import DSTextField from "../../../system-design/DSTextField";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSCircularProgress from "../../../system-design/DSCircularProgress";

const LogoModal = (props: { logo: PendulumLogo; onClose: () => void }) => {
  const [name, setName] = useState<string>(props.logo.name || "");
  const [description, setDescription] = useState<string>(
    props.logo.description || "",
  );
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { mutateAsync: updateLogoMutation, isPending: isSaving } =
    useUpdateLogoMutation();
  const { mutateAsync: deleteLogoMutation, isPending: isDeleting } =
    useDeleteLogoMutation();

  const saveLogo = async () => {
    updateLogoMutation({
      id: props.logo.id,
      logo: {
        name,
        description,
      },
    });
  };

  return (
    <>
      <DSDialog open onClose={props.onClose} fullWidth maxWidth="md">
        <DSDialogTitle>Edit Logo Details</DSDialogTitle>
        <DSDialogContent>
          <form noValidate id="editLogoForm">
            <DSStack spacing={3} sx={{ mt: 1 }}>
              <DSBox>
                <DSTypography sx={{ fontWeight: "bold", mb: 1 }}>
                  Current Logo:
                </DSTypography>
                <DSBox sx={{ textAlign: "center" }}>
                  <img
                    src={props.logo.url}
                    alt={props.logo.filename}
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      padding: "10px",
                    }}
                  />
                </DSBox>
                <DSTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 1 }}
                >
                  Filename: {props.logo.filename}
                </DSTypography>
              </DSBox>

              <DSTextField
                label="Logo Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter logo name..."
                fullWidth
              />

              <DSTextField
                label="Description"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
                fullWidth
              />

              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <DSErrorButton onClick={() => setShowDeleteModal(true)}>
                  Delete Logo
                </DSErrorButton>
                <DSBox sx={{ display: "flex", gap: 1 }}>
                  <DSSecondaryButton onClick={props.onClose}>
                    Cancel
                  </DSSecondaryButton>
                  <DSPrimaryButton onClick={() => setShowSaveModal(true)}>
                    Save Changes
                  </DSPrimaryButton>
                </DSBox>
              </DSBox>
            </DSStack>
          </form>
        </DSDialogContent>
      </DSDialog>

      {showSaveModal && (
        <DSDialog onClose={() => setShowSaveModal(false)} open fullWidth>
          <DSDialogTitle>Confirm Changes</DSDialogTitle>
          <DSDialogContent>
            <DSTypography variant="h6" sx={{ textAlign: "center", m: 3 }}>
              Save changes to "{name || props.logo.filename}"?
            </DSTypography>
          </DSDialogContent>
          <DSDialogActions sx={{ justifyContent: "space-between" }}>
            <DSSecondaryButton onClick={() => setShowSaveModal(false)}>
              Cancel
            </DSSecondaryButton>
            <DSPrimaryButton
              disabled={isSaving}
              startIcon={
                isSaving ? <DSCircularProgress size={20} /> : undefined
              }
              onClick={() =>
                saveLogo().then(() => {
                  setShowSaveModal(false);
                  props.onClose();
                })
              }
            >
              Save Changes
            </DSPrimaryButton>
          </DSDialogActions>
        </DSDialog>
      )}

      {showDeleteModal && (
        <DSDialog onClose={() => setShowDeleteModal(false)} open fullWidth>
          <DSDialogTitle>Delete Logo</DSDialogTitle>
          <DSDialogContent>
            <DSBox sx={{ m: 3 }}>
              <DSTypography variant="h6" sx={{ textAlign: "center" }}>
                Are you sure you want to delete "{name || props.logo.filename}
                "?
              </DSTypography>
              <DSTypography sx={{ textAlign: "center" }} color="text.secondary">
                This action cannot be undone.
              </DSTypography>
            </DSBox>
          </DSDialogContent>
          <DSDialogActions sx={{ justifyContent: "space-between" }}>
            <DSSecondaryButton onClick={() => setShowDeleteModal(false)}>
              Cancel
            </DSSecondaryButton>
            <DSErrorButton
              disabled={isDeleting}
              startIcon={
                isDeleting ? <DSCircularProgress size={20} /> : undefined
              }
              onClick={() =>
                deleteLogoMutation(props.logo.id, {
                  onSuccess: () => {
                    setShowDeleteModal(false);
                    props.onClose();
                  },
                }).catch(() => undefined)
              }
            >
              Delete Logo
            </DSErrorButton>
          </DSDialogActions>
        </DSDialog>
      )}
    </>
  );
};

export default LogoModal;
